import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="page-search"
export default class extends Controller {
  static targets = [
    "form",
    "searchField",
    "criteriaCheckBox",
    "filterRadioButton",
    "criteriaErrorMessage",
    "criteriaLabel",
    "criteriaContainer",
  ];

  static values = { searched: Boolean };

  static hasCriteriaErrors = false;

  static storedFormValues = {
    searchField: "",
    criteriaCheckBoxes: [],
    filterRadioButtons: [],
  };

  connect() {
    this.#restoreFormValues();
    this.#toggleCriteriaContainer();
    this.#updateCriteriaSelectionErrorDisplay();
  }

  disconnect() {
    this.#storeFormValues();
  }

  submit(event) {
    this.#sanitizeFormBeforeSubmit();

    // Allow default form submission if valid or reset
    if (this.isFormFilled() || this.isPreviousSearchReset()) {
      return true;
    }

    event.preventDefault();
    event.stopPropagation();
  }

  #sanitizeFormBeforeSubmit() {
    this.constructor.hasCriteriaErrors = false;

    // Uncheck criteria if no text, else show error if no criteria selected
    if (!this.hasText()) {
      this.#uncheckCriteriaTargets();
    } else if (!this.hasCriteria()) {
      this.constructor.hasCriteriaErrors = true;
    }

    this.#updateCriteriaSelectionErrorDisplay();
  }

  // Helper Methods
  hasText() {
    return this.searchFieldTarget.value.trim().length > 0; // Check for non-empty text
  }

  hasCriteria() {
    return this.criteriaCheckBoxTargets.some((target) => target.checked); // Check if any criteria is selected
  }

  hasFilter() {
    return this.filterRadioButtonTargets.some((target) => target.checked); // Check if any filter is applied
  }

  hasPreviouslySearched() {
    return this.searchedValue; // Check if a search was performed before
  }

  isPreviousSearchReset() {
    return this.hasPreviouslySearched() && this.isFormEmpty(); // True if resetting a previous search
  }

  isFormFilled() {
    if (this.hasText()) {
      return this.hasCriteria();
    }

    return this.hasFilter();
  }

  isFormEmpty() {
    return !this.hasFilter() && !this.hasText() && !this.hasCriteria(); // Check if form is empty
  }

  #uncheckCriteriaTargets() {
    this.criteriaCheckBoxTargets.forEach((target) => (target.checked = false)); // Uncheck all criteria
  }

  #updateCriteriaSelectionErrorDisplay() {
    if (this.constructor.hasCriteriaErrors) {
      this.#renderCriteriaSelectionError();
    } else {
      this.#hideCriteriaSelectionError();
    }
  }

  #renderCriteriaSelectionError() {
    this.criteriaErrorMessageTarget.style.display = "block"; // Show criteria error message
    this.criteriaLabelTargets.forEach((label) =>
      label.classList.add("fieldset_error__input")
    );
  }

  #hideCriteriaSelectionError() {
    this.criteriaErrorMessageTarget.style.display = "none"; // Hide criteria error message
    this.criteriaLabelTargets.forEach((label) =>
      label.classList.remove("fieldset_error__input")
    );
  }

  #toggleCriteriaContainer() {
    this.criteriaContainerTarget.style.display =
      this.hasText() || this.hasCriteria() ? "block" : "none"; // Show/hide criteria container based on selection
  }

  #restoreFormValues() {
    const { searchField, criteriaCheckBoxes, filterRadioButtons } =
      this.constructor.storedFormValues;

    this.searchFieldTarget.value = searchField || "";
    this.criteriaCheckBoxTargets.forEach((target, index) => {
      target.checked = criteriaCheckBoxes[index] || false;
    });
    this.filterRadioButtonTargets.forEach((target, index) => {
      target.checked = filterRadioButtons[index] || false;
    });
  }

  #storeFormValues() {
    this.constructor.storedFormValues = {
      searchField: this.searchFieldTarget.value,
      criteriaCheckBoxes: this.criteriaCheckBoxTargets.map(
        (target) => target.checked
      ),
      filterRadioButtons: this.filterRadioButtonTargets.map(
        (target) => target.checked
      ),
    };
  }
}
